import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import DieMiniature from './DieMiniature';
import { playSound } from '../../utils/utils';
import './LastStandRescue.css';

const LastStandRescue = ({
  dieId,
  impactSeen,
  onImpact,
  onContinue,
  reduceMotion,
}) => {
  const [beat, setBeat] = useState(reduceMotion ? 3 : impactSeen ? 2 : 0);
  const title = useRef(null);
  const impact = useRef(onImpact);
  impact.current = onImpact;
  useEffect(() => {
    title.current?.focus({ preventScroll: true });
    if (reduceMotion) {
      if (!impactSeen) impact.current();
      return undefined;
    }
    if (impactSeen) {
      const finish = setTimeout(() => setBeat(3), 1300);
      return () => clearTimeout(finish);
    }
    const timers = [
      setTimeout(() => {
        setBeat(1);
        impact.current();
        playSound('/sounds/dice-hits-board.mp3');
      }, 600),
      setTimeout(() => {
        setBeat(2);
        playSound('/sounds/shield-charge-up.mp3');
      }, 1600),
      setTimeout(() => setBeat(3), 2900),
    ];
    return () => timers.forEach(clearTimeout);
    // An impact is saved mid-scene; it must not restart the running animation.
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return createPortal(
    <section
      className={`coreboundLastStandRescue at-${beat} ${
        reduceMotion ? 'is-reduced' : ''
      }`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="lastStandRescueTitle"
      onKeyDown={(event) => {
        if (event.key !== 'Tab') return;
        event.preventDefault();
        (event.currentTarget.querySelector('button') || title.current)?.focus();
      }}
    >
      <div className="coreboundRescueArt" aria-hidden="true">
        <div className="coreboundRescueFatalBeam" />
        <div className="coreboundRescueImpact" />
        <div className="coreboundRescueDie">
          <DieMiniature dieId={dieId} rotating={beat >= 2} />
        </div>
        <svg className="coreboundRescueThreads" viewBox="0 0 600 400">
          <defs>
            <radialGradient id="rescueLight">
              <stop stopColor="#ffe9a3" stopOpacity=".5" />
              <stop offset="1" stopColor="#ffe9a3" stopOpacity="0" />
            </radialGradient>
          </defs>
          <ellipse
            cx="300"
            cy="195"
            rx="215"
            ry="180"
            fill="url(#rescueLight)"
            stroke="none"
          />
          <path d="M300 350 C130 290 170 90 300 120 C430 90 470 290 300 350" />
          <path d="M300 350 C470 230 100 210 300 120 C500 210 130 230 300 350" />
          <ellipse cx="300" cy="173" rx="100" ry="35" />
          <path
            d="M190 190 214 125 300 86 386 125 410 190 350 248 250 248Z"
            strokeDasharray="3 14"
          />
          {[
            [190, 190],
            [214, 125],
            [300, 86],
            [386, 125],
            [410, 190],
            [350, 248],
            [250, 248],
          ].map(([cx, cy]) => (
            <circle key={`${cx}:${cy}`} cx={cx} cy={cy} r="4" fill="#fff1b7" />
          ))}
        </svg>
        <div className="coreboundRescueRelic">
          <svg viewBox="0 0 80 80">
            <path d="M40 5 68 23 62 60 40 75 18 60 12 23Z" />
            <path d="M25 22H55L29 58H51M26 22 53 58" />
          </svg>
        </div>
      </div>
      <div className="coreboundRescueStory" aria-live="polite">
        <span>LAST STAND · YOUR RELIC AWAKENS</span>
        <h1 ref={title} tabIndex={-1} id="lastStandRescueTitle">
          {beat < 2 ? 'One spark remains.' : 'Your story isn’t over.'}
        </h1>
        <p>
          {beat < 2
            ? 'The fatal blow lands. As your die begins to break, a golden light answers.'
            : 'Your Last Stand relic catches the last spark inside your die and holds it together. It can give you one final throw.'}
        </p>
        {beat >= 3 && (
          <>
            <p className="coreboundRescueRule">
              Score <strong>76 or higher</strong> to return with{' '}
              <strong>1 HP</strong>. You have one chance.
            </p>
            <button
              type="button"
              className="coreboundPrimary"
              onClick={onContinue}
            >
              Take my last chance →
            </button>
          </>
        )}
      </div>
    </section>,
    document.body
  );
};

export default LastStandRescue;
